// Cookie-consent core: state shape, persistence, and Google Consent Mode v2
// wiring. Intentionally framework-free so it can run before React mounts and
// be reused anywhere. The React layer lives in src/providers/consent-provider.tsx.

import { LOCAL_STORAGE_KEYS } from './constants'

/** Consent categories surfaced in the banner. Necessary is always on. */
export type ConsentCategories = {
  necessary: true
  analytics: boolean
  marketing: boolean
}

/** Stored record. `version` lets us re-prompt if the policy/categories change. */
export type StoredConsent = {
  categories: ConsentCategories
  timestamp: number
  version: number
}

/** Bump to invalidate prior consent and re-show the banner to everyone. */
export const CONSENT_VERSION = 1

/** Fired on the window whenever consent changes, so already-mounted pages
 *  (e.g. the HubSpot forms) can react without a reload. */
export const CONSENT_EVENT = 'ent-consent-change'

export const DENIED_CONSENT: ConsentCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
}

export const FULL_CONSENT: ConsentCategories = {
  necessary: true,
  analytics: true,
  marketing: true,
}

type ConsentEventDetail = ConsentCategories

declare global {
  interface WindowEventMap {
    [CONSENT_EVENT]: CustomEvent<ConsentEventDetail>
  }
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

/** Read the persisted choice, or null if the visitor hasn't responded (or the
 *  stored version is stale). */
export function readConsent(): ConsentCategories | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEYS.COOKIE_CONSENT)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredConsent
    if (!parsed?.categories || parsed.version !== CONSENT_VERSION) return null
    return {
      necessary: true,
      analytics: Boolean(parsed.categories.analytics),
      marketing: Boolean(parsed.categories.marketing),
    }
  } catch {
    return null
  }
}

/** Persist the choice (necessary is always coerced to true). */
export function writeConsent(categories: ConsentCategories): void {
  if (typeof window === 'undefined') return
  const record: StoredConsent = {
    categories: { ...categories, necessary: true },
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  }
  try {
    window.localStorage.setItem(
      LOCAL_STORAGE_KEYS.COOKIE_CONSENT,
      JSON.stringify(record),
    )
  } catch {
    /* storage unavailable (private mode) — consent simply won't persist */
  }
}

/** Push the Consent Mode v2 update signals and notify the rest of the app. */
export function applyConsent(categories: ConsentCategories): void {
  if (typeof window === 'undefined') return

  // gtag is defined inline in index.html; fall back to a dataLayer push so this
  // is robust even if the named global isn't present yet.
  const update = {
    analytics_storage: categories.analytics ? 'granted' : 'denied',
    ad_storage: categories.marketing ? 'granted' : 'denied',
    ad_user_data: categories.marketing ? 'granted' : 'denied',
    ad_personalization: categories.marketing ? 'granted' : 'denied',
  } as const

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', update)
  } else {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(['consent', 'update', update])
  }

  window.dispatchEvent(
    new CustomEvent<ConsentEventDetail>(CONSENT_EVENT, { detail: categories }),
  )
}

/** Persist + apply in one step. */
export function saveConsent(categories: ConsentCategories): void {
  writeConsent(categories)
  applyConsent(categories)
}
